<template>
  <div class="policy-detail">
    <div class="page-header">
      <el-button size="small" @click="$router.push('/accounting-policy')">← 返回列表</el-button>
      <h2 style="margin:0">{{ policy.policyName }}</h2>
      <el-tag :type="statusTag(policy.status)" size="small">{{ statusLabel(policy.status) }}</el-tag>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="策略信息" name="info">
        <el-form label-width="120px" size="small">
          <el-form-item label="策略编码">{{ policy.policyCode }}</el-form-item>
          <el-form-item label="业务场景">{{ sceneLabel(policy.businessScene) }}</el-form-item>
          <el-form-item label="描述">{{ policy.description || '-' }}</el-form-item>
          <el-form-item label="当前版本">v{{ policy.activeVersion || '-' }}</el-form-item>
          <el-form-item label="状态">{{ statusLabel(policy.status) }}</el-form-item>
        </el-form>
        <div style="margin-top:12px">
          <el-button type="primary" size="small" @click="publishPolicy" :disabled="policy.status !== 'DRAFT'">发布策略</el-button>
          <el-button type="danger" size="small" @click="archivePolicy" :disabled="policy.status === 'ARCHIVED'">归档策略</el-button>
          <el-button type="success" size="small" @click="showAddRule = true" style="margin-left:12px">新增规则</el-button>
        </div>
        <p style="color:#909399;font-size:12px;margin-top:12px">
          ⚠ 当前配置暂为 shadow config，尚未替换内置 Java 规则执行。
        </p>
      </el-tab-pane>

      <el-tab-pane label="规则配置" name="rules">
        <el-table :data="rules" stripe size="small">
          <el-table-column prop="ruleName" label="规则名称" min-width="140" />
          <el-table-column prop="ruleCode" label="编码" width="160" />
          <el-table-column prop="businessType" label="业务类型" width="120" />
          <el-table-column prop="decisionType" label="决策类型" width="160" />
          <el-table-column prop="priority" label="优先级" width="70" align="center" />
          <el-table-column label="启用" width="70" align="center">
            <template slot-scope="scope">
              <el-switch v-model="scope.row.enabled" @change="toggleRule(scope.row)" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="editRule(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="版本记录" name="versions">
        <el-table :data="versions" stripe size="small">
          <el-table-column prop="versionNo" label="版本号" width="80">
            <template slot-scope="scope">v{{ scope.row.versionNo }}</template>
          </el-table-column>
          <el-table-column prop="versionName" label="名称" width="120" />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag size="small" :type="scope.row.status === 'PUBLISHED' ? 'success' : 'info'">{{ scope.row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="publishedAt" label="发布时间" width="170" :formatter="dateFormatter" />
          <el-table-column prop="remark" label="备注" />
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="模拟执行" name="simulate">
        <div class="simulate-section">
          <p style="color:#e6a23c;font-size:13px;margin-bottom:12px">
            ⚠ 当前为策略模拟执行，仅用于配置验证，不影响真实银行流水转凭证结果。
          </p>
          <el-form :model="simForm" label-width="140px" size="small">
            <el-form-item label="业务类型">
              <el-select v-model="simForm.businessType" style="width:200px">
                <el-option label="采购付款" value="PURCHASE_PAYMENT" />
                <el-option label="物流付款" value="LOGISTICS_PAYMENT" />
              </el-select>
            </el-form-item>
            <el-form-item label="付款金额">
              <el-input v-model="simForm.paymentAmount" style="width:200px" placeholder="例: 30000.00" />
            </el-form-item>
            <el-form-item label="对手方名称">
              <el-input v-model="simForm.counterpartyName" style="width:300px" placeholder="例: 深圳云帆科技有限公司" />
            </el-form-item>
            <el-form-item label="对手方ID">
              <el-input v-model="simForm.counterpartyEntityId" style="width:200px" placeholder="例: ent-1009" />
            </el-form-item>
            <el-form-item label="期间">
              <el-input v-model="simForm.period" style="width:150px" placeholder="例: 2026-05" />
            </el-form-item>
            <el-form-item label="证据样本 JSON">
              <el-input type="textarea" v-model="simForm.evidenceJson" :rows="4" style="width:100%"
                placeholder='[{"source":"REFERENCE_FACT_PURCHASE_PAYABLE_SUMMARY","fields":{"endingBalance":"150000.00","paymentAmount":"30000.00"}}]' />
            </el-form-item>
            <el-form-item>
              <el-checkbox v-model="simForm.compareWithBuiltin" style="margin-bottom:8px">同时对比内置 Java 规则</el-checkbox>
              <div>
                <el-button type="primary" @click="runSimulate" :loading="simulating">模拟执行</el-button>
              </div>
            </el-form-item>
          </el-form>

          <div v-if="simResult" class="simulate-result">
            <h4>模拟结果</h4>
            <p><strong>匹配状态：</strong>
              <el-tag :type="simResult.matched ? 'success' : 'info'">{{ simResult.matched ? '已匹配' : '未匹配' }}</el-tag>
            </p>
            <p v-if="simResult.policyCode"><strong>策略：</strong>{{ simResult.policyName || simResult.policyCode }}</p>

            <div v-if="simResult.matchedRules && simResult.matchedRules.length > 0">
              <h4>匹配规则</h4>
              <el-table :data="simResult.matchedRules" stripe size="small" style="width:100%">
                <el-table-column prop="ruleName" label="规则名称" width="140" />
                <el-table-column prop="decisionType" label="决策类型" width="140" />
                <el-table-column prop="riskLevel" label="风险等级" width="100">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.riskLevel === 'PASS' ? 'success' : scope.row.riskLevel === 'WARNING' ? 'warning' : 'danger'" size="small">{{ scope.row.riskLevel }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="reason" label="说明" min-width="300" />
              </el-table>
            </div>

            <div v-if="simResult.blockedReasons && simResult.blockedReasons.length > 0">
              <h4>阻断原因</h4>
              <ul><li v-for="r in simResult.blockedReasons" :key="r">{{ r }}</li></ul>
            </div>

            <div v-if="simResult.warnings && simResult.warnings.length > 0">
              <h4>提示</h4>
              <ul><li v-for="w in simResult.warnings" :key="w" style="color:#909399">{{ w }}</li></ul>
            </div>
          </div>

          <div v-if="compareResult" class="simulate-result" style="margin-top:24px;border-top:2px solid #409EFF;padding-top:16px">
            <h3 style="color:#409EFF">策略模拟 vs 内置 Java 规则 对比</h3>
            <p style="color:#e6a23c;font-size:13px;margin-bottom:12px">
              ⚠ 当前为策略模拟与内置规则对比，仅用于配置验证，不影响真实银行流水转凭证结果。
            </p>

            <el-row :gutter="20">
              <el-col :span="12">
                <div style="border:1px solid #ebeef5;padding:12px;border-radius:4px">
                  <h4>策略模拟结果</h4>
                  <p><strong>匹配状态：</strong>
                    <el-tag :type="compareResult.policySimulation.matched ? 'success' : 'info'" size="small">
                      {{ compareResult.policySimulation.matched ? '已匹配' : '未匹配' }}
                    </el-tag>
                  </p>
                  <div v-if="compareResult.policySimulation.matchedRules && compareResult.policySimulation.matchedRules.length > 0">
                    <el-table :data="compareResult.policySimulation.matchedRules" stripe size="small" style="width:100%">
                      <el-table-column prop="ruleCode" label="规则编码" width="120" />
                      <el-table-column prop="decisionType" label="决策类型" width="120" />
                      <el-table-column prop="riskLevel" label="风险等级" width="80">
                        <template slot-scope="scope">
                          <el-tag :type="riskTagType(scope.row.riskLevel)" size="small">{{ scope.row.riskLevel }}</el-tag>
                        </template>
                      </el-table-column>
                      <el-table-column prop="reason" label="说明" min-width="160" />
                    </el-table>
                  </div>
                  <div v-if="compareResult.policySimulation.warnings && compareResult.policySimulation.warnings.length > 0">
                    <p style="color:#909399;font-size:12px" v-for="w in compareResult.policySimulation.warnings" :key="w">{{ w }}</p>
                  </div>
                </div>
              </el-col>
              <el-col :span="12">
                <div style="border:1px solid #ebeef5;padding:12px;border-radius:4px">
                  <h4>内置 Java 规则结果</h4>
                  <p><strong>匹配状态：</strong>
                    <el-tag :type="compareResult.builtinRuleResult.matched ? 'success' : 'info'" size="small">
                      {{ compareResult.builtinRuleResult.matched ? '已匹配' : '未匹配' }}
                    </el-tag>
                  </p>
                  <p v-if="compareResult.builtinRuleResult.ruleName"><strong>规则名称：</strong>{{ compareResult.builtinRuleResult.ruleName }}</p>
                  <p v-if="compareResult.builtinRuleResult.matched"><strong>决策类型：</strong>
                    <el-tag size="small">{{ compareResult.builtinRuleResult.decisionType }}</el-tag>
                  </p>
                  <p v-if="compareResult.builtinRuleResult.matched"><strong>风险等级：</strong>
                    <el-tag :type="riskTagType(compareResult.builtinRuleResult.riskLevel)" size="small">
                      {{ compareResult.builtinRuleResult.riskLevel }}
                    </el-tag>
                  </p>
                  <p v-if="compareResult.builtinRuleResult.reason"><strong>说明：</strong>{{ compareResult.builtinRuleResult.reason }}</p>
                  <div v-if="compareResult.builtinRuleResult.blockedReasons && compareResult.builtinRuleResult.blockedReasons.length > 0">
                    <p style="color:#F56C6C"><strong>阻断原因：</strong></p>
                    <ul><li v-for="r in compareResult.builtinRuleResult.blockedReasons" :key="r" style="color:#F56C6C">{{ r }}</li></ul>
                  </div>
                </div>
              </el-col>
            </el-row>

            <div v-if="compareResult.comparison" style="margin-top:16px;padding:12px;background:#f5f7fa;border-radius:4px">
              <h4>差异分析</h4>
              <el-table :data="[compareResult.comparison]" stripe size="small" style="width:100%">
                <el-table-column label="匹配状态一致" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.sameMatched ? 'success' : 'danger'" size="small">{{ scope.row.sameMatched ? '是' : '否' }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="决策类型一致" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.sameDecisionType ? 'success' : 'danger'" size="small">{{ scope.row.sameDecisionType ? '是' : '否' }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="风险等级一致" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.sameRiskLevel ? 'success' : 'danger'" size="small">{{ scope.row.sameRiskLevel ? '是' : '否' }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="策略风险等级" width="100" align="center">
                  <template slot-scope="scope">
                    <span v-if="scope.row.riskLevelDiff">{{ scope.row.riskLevelDiff.policy }}</span>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column label="内置风险等级" width="100" align="center">
                  <template slot-scope="scope">
                    <span v-if="scope.row.riskLevelDiff">{{ scope.row.riskLevelDiff.builtin }}</span>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="compareResult.comparison.warnings && compareResult.comparison.warnings.length > 0" style="margin-top:8px">
                <p v-for="w in compareResult.comparison.warnings" :key="w" :style="{color: w.indexOf('一致') > 0 ? '#67C23A' : '#e6a23c', fontSize:'13px'}">{{ w }}</p>
              </div>
            </div>

            <div v-if="compareResult.safetyWarnings && compareResult.safetyWarnings.length > 0" style="margin-top:12px">
              <p v-for="w in compareResult.safetyWarnings" :key="w" style="color:#e6a23c;font-size:12px">⚠ {{ w }}</p>
            </div>

            <div v-if="compareResult.unusedInputs && compareResult.unusedInputs.length > 0" style="margin-top:8px">
              <p style="color:#909399;font-size:12px">
                💡 以下字段当前未参与后端模拟判定：{{ compareResult.unusedInputs.join('、') }}。这些字段主要用于构造模拟上下文和后续对比展示，只有 rule_config_json 中引用相关字段时才参与条件判断。
              </p>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="样例库 / 回归验证" name="cases">
        <div class="cases-section">
          <p style="color:#e6a23c;font-size:13px;margin-bottom:12px">
            ⚠ 样例库仅用于策略模拟与回归验证，不影响真实凭证生成。
          </p>
          <div style="margin-bottom:12px">
            <el-button type="primary" size="small" @click="showCaseForm = true; caseForm = defaultCaseForm()">新建样例</el-button>
            <el-button type="success" size="small" @click="runBatchCases" :loading="batchRunning">批量回归测试</el-button>
            <el-button type="warning" size="small" @click="seedStandardCases" :loading="seeding" style="margin-left:8px">初始化标准样例</el-button>
          </div>
          <el-table :data="cases" stripe size="small">
            <el-table-column prop="caseName" label="样例名称" width="140" />
            <el-table-column prop="caseCode" label="编码" width="100" />
            <el-table-column prop="businessType" label="业务类型" width="100" />
            <el-table-column label="预期决策类型" width="110">
              <template slot-scope="scope">{{ scope.row.expectedDecisionType || '-' }}</template>
            </el-table-column>
            <el-table-column label="预期风险等级" width="100">
              <template slot-scope="scope">
                <span v-if="scope.row.expectedRiskLevel">
                  <el-tag :type="riskTagType(scope.row.expectedRiskLevel)" size="small">{{ scope.row.expectedRiskLevel }}</el-tag>
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="预期匹配" width="80" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.expectedMatched !== null">{{ scope.row.expectedMatched ? '是' : '否' }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="70" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 'ACTIVE' ? 'success' : 'info'" size="small">{{ scope.row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="最后执行" width="160">
              <template slot-scope="scope">
                <span v-if="scope.row.lastRunStatus">
                  <el-tag :type="scope.row.lastRunStatus === 'PASS' ? 'success' : scope.row.lastRunStatus === 'WARNING' ? 'warning' : 'danger'" size="small">{{ scope.row.lastRunStatus }}</el-tag>
                </span>
                <span v-else style="color:#909399">未执行</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="280" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="editCase(scope.row)">编辑</el-button>
                <el-button size="mini" type="text" @click="runCaseSimulate(scope.row)">模拟</el-button>
                <el-button size="mini" type="text" @click="runCaseCompare(scope.row)">对比</el-button>
                <el-button size="mini" type="text" @click="viewCaseRuns(scope.row)">执行记录</el-button>
                <el-button v-if="scope.row.status === 'ACTIVE'" size="mini" type="text" style="color:#F56C6C" @click="archiveCase(scope.row)">归档</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <el-tab-pane label="审计日志" name="audit">
        <el-table :data="auditLogs" stripe size="small">
          <el-table-column prop="operation" label="操作" width="140">
            <template slot-scope="scope">
              <el-tag size="small">{{ scope.row.operation }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="200" />
          <el-table-column prop="operatedAt" label="操作时间" width="170" :formatter="dateFormatter" />
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="caseForm.id ? '编辑样例' : '新建样例'" :visible.sync="showCaseForm" width="700px">
      <el-form label-width="120px" size="small">
        <el-form-item label="样例编码"><el-input v-model="caseForm.caseCode" placeholder="CASE_XXX" /></el-form-item>
        <el-form-item label="样例名称"><el-input v-model="caseForm.caseName" /></el-form-item>
        <el-form-item label="业务类型">
          <el-select v-model="caseForm.businessType" style="width:100%">
            <el-option label="采购付款" value="PURCHASE_PAYMENT" />
            <el-option label="物流付款" value="LOGISTICS_PAYMENT" />
          </el-select>
        </el-form-item>
        <el-form-item label="说明"><el-input v-model="caseForm.description" /></el-form-item>
        <el-form-item label="请求 JSON">
          <el-input type="textarea" v-model="caseForm.requestJson" :rows="6" />
          <p style="color:#909399;font-size:12px">SimulateRequest JSON，可使用<a style="cursor:pointer;color:#409EFF" @click="fillCaseFromSimForm">当前模拟参数填充</a>或<a style="cursor:pointer;color:#409EFF" @click="fillCaseTemplate">采购应付模板</a></p>
        </el-form-item>
        <el-form-item label="预期决策类型">
          <el-select v-model="caseForm.expectedDecisionType" style="width:200px" clearable placeholder="可选">
            <el-option v-for="dt in decisionTypes" :key="dt" :label="dt" :value="dt" />
          </el-select>
        </el-form-item>
        <el-form-item label="预期风险等级">
          <el-select v-model="caseForm.expectedRiskLevel" style="width:200px" clearable placeholder="可选">
            <el-option label="PASS" value="PASS" />
            <el-option label="WARNING" value="WARNING" />
            <el-option label="BLOCKER" value="BLOCKER" />
          </el-select>
        </el-form-item>
        <el-form-item label="预期匹配">
          <el-switch v-model="caseForm.expectedMatched" :active-value="true" :inactive-value="false" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showCaseForm = false">取消</el-button>
        <el-button type="primary" @click="saveCase">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="'执行记录 - ' + (runsCaseName || '')" :visible.sync="showRuns" width="800px">
      <el-table :data="runs" stripe size="small">
        <el-table-column prop="runType" label="类型" width="120">
          <template slot-scope="scope">
            <el-tag size="small">{{ scope.row.runType }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.resultStatus === 'PASS' ? 'success' : scope.row.resultStatus === 'WARNING' ? 'warning' : 'danger'" size="small">{{ scope.row.resultStatus }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="decisionType" label="决策类型" width="120" />
        <el-table-column prop="riskLevel" label="风险等级" width="80" />
        <el-table-column prop="matched" label="匹配" width="60" align="center">
          <template slot-scope="scope">{{ scope.row.matched === true ? '是' : scope.row.matched === false ? '否' : '-' }}</template>
        </el-table-column>
        <el-table-column prop="diffSummary" label="差异" min-width="160" />
        <el-table-column prop="runAt" label="执行时间" width="160" :formatter="dateFormatter" />
      </el-table>
    </el-dialog>

    <el-dialog title="新增规则" :visible.sync="showAddRule" width="600px">
      <el-form label-width="120px" size="small">
        <el-form-item label="规则名称"><el-input v-model="ruleForm.ruleName" /></el-form-item>
        <el-form-item label="规则编码"><el-input v-model="ruleForm.ruleCode" placeholder="RULE_XXX" /></el-form-item>
        <el-form-item label="决策类型">
          <el-select v-model="ruleForm.decisionType" style="width:100%">
            <el-option v-for="dt in decisionTypes" :key="dt" :label="dt" :value="dt" />
          </el-select>
        </el-form-item>
        <el-form-item label="优先级"><el-input-number v-model="ruleForm.priority" :min="1" :max="99" /></el-form-item>
        <el-form-item label="JSON配置">
          <el-input type="textarea" v-model="ruleForm.ruleConfigJson" :rows="6" placeholder='{"action":{"manualReviewRequired":true}}' />
          <p style="color:#909399;font-size:12px">必须为合法 JSON，可使用常用模板：<el-button size="mini" type="text" @click="fillJsonTemplate('ap')">应付模板</el-button></p>
        </el-form-item>
        <el-form-item label="描述"><el-input v-model="ruleForm.description" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showAddRule = false">取消</el-button>
        <el-button type="primary" @click="saveRule">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api/accountingPolicy';
import {mapState} from 'vuex';

export default {
  computed: {
    ...mapState(['currentAccountSets'])
  },
  data() {
    return {
      policyId: this.$route.params.id,
      policy: {},
      rules: [],
      versions: [],
      auditLogs: [],
      activeTab: 'info',
      showAddRule: false,
      ruleForm: { ruleName: '', ruleCode: '', decisionType: 'PAY_AP', priority: 1, ruleConfigJson: '', description: '' },
      decisionTypes: ['PAY_AP','PAY_PREPAYMENT','REFUND_OFFSET','OVERPAY_EXCEPTION',
        'HEAD_FREIGHT','TAIL_FREIGHT','LOGISTICS_COMPOSITE','COMPENSATION_OFFSET',
        'SPLIT_REQUIRED','LOGISTICS_PAYMENT_CANDIDATE','PURCHASE_PAYMENT_CANDIDATE'],
      simForm: { businessType: 'PURCHASE_PAYMENT', paymentAmount: '', counterpartyName: '', counterpartyEntityId: '', period: '', evidenceJson: '', compareWithBuiltin: false },
      simResult: null,
      compareResult: null,
      simulating: false,
      cases: [],
      showCaseForm: false,
      caseForm: {},
      showRuns: false,
      runs: [],
      runsCaseName: '',
      batchRunning: false,
      seeding: false
    };
  },
  mounted() {
    this.loadPolicy();
    this.loadRules();
    this.loadVersions();
    this.loadAuditLogs();
  },
  watch: {
    activeTab(tab) {
      if (tab === 'cases') this.loadCases();
    }
  },
  methods: {
    loadPolicy() { api.getPolicySet(this.policyId).then(res => { this.policy = res.data || {}; }); },
    loadRules() { api.listRules(this.policyId).then(res => { this.rules = (res.data || []).map(r => ({ ...r, enabled: r.enabled !== false })); }); },
    loadVersions() { api.listVersions(this.policyId).then(res => { this.versions = res.data || []; }); },
    loadAuditLogs() { api.listAuditLogs(this.policyId).then(res => { this.auditLogs = res.data || []; }); },
    publishPolicy() {
      this.$confirm('发布后将生成版本快照，确认发布？', '提示').then(() => {
        api.publishPolicy(this.policyId, {}).then(() => { this.$message.success('发布成功'); this.loadPolicy(); this.loadVersions(); });
      }).catch(() => {});
    },
    archivePolicy() {
      this.$confirm('归档后无法修改，确认归档？', '提示').then(() => {
        api.archivePolicy(this.policyId, { remark: '手动归档' }).then(() => { this.$message.success('归档成功'); this.loadPolicy(); });
      }).catch(() => {});
    },
    toggleRule(rule) {
      if (rule.enabled) { api.enableRule(rule.id).catch(() => { rule.enabled = false; }); }
      else { api.disableRule(rule.id).catch(() => { rule.enabled = true; }); }
    },
    editRule(rule) {
      this.ruleForm = { ...rule };
      this.showAddRule = true;
    },
    saveRule() {
      if (this.ruleForm.id) {
        api.updateRule(this.ruleForm.id, this.ruleForm).then(() => {
          this.$message.success('更新成功'); this.showAddRule = false; this.loadRules();
        }).catch(() => { this.$message.error('保存失败，请检查 JSON 格式'); });
      } else {
        api.createRule(this.policyId, this.ruleForm).then(() => {
          this.$message.success('创建成功'); this.showAddRule = false; this.loadRules();
        }).catch(() => { this.$message.error('创建失败，请检查 JSON 格式'); });
      }
    },
    fillJsonTemplate(type) {
      if (type === 'ap') { this.ruleForm.ruleConfigJson = JSON.stringify({ action: { manualReviewRequired: true }, conflictPolicy: { multipleFactsConflict: 'BLOCKER' } }, null, 2); }
    },
    runSimulate() {
      let evidenceSamples = [];
      if (this.simForm.evidenceJson) {
        try { evidenceSamples = JSON.parse(this.simForm.evidenceJson); } catch (e) { this.$message.error('证据样本 JSON 格式错误'); return; }
      }
      const requestData = {
        accountSetsId: this.currentAccountSets ? this.currentAccountSets.id : 66,
        policyCode: this.policy.policyCode,
        businessType: this.simForm.businessType || undefined,
        paymentAmount: this.simForm.paymentAmount || undefined,
        counterpartyName: this.simForm.counterpartyName || undefined,
        counterpartyEntityId: this.simForm.counterpartyEntityId || undefined,
        period: this.simForm.period || undefined,
        evidenceSamples: evidenceSamples.length > 0 ? evidenceSamples : undefined
      };
      this.simulating = true;
      this.compareResult = null;
      if (this.simForm.compareWithBuiltin) {
        api.compare(requestData).then(res => {
          this.simResult = null;
          this.compareResult = res.data;
          this.$message.success('模拟对比完成');
        }).catch(() => {
          this.$message.error('模拟对比失败');
        }).finally(() => { this.simulating = false; });
      } else {
        api.simulate(requestData).then(res => {
          this.simResult = res.data;
          this.compareResult = null;
          this.$message.success('模拟执行完成');
        }).catch(() => {
          this.$message.error('模拟执行失败');
        }).finally(() => { this.simulating = false; });
      }
    },
    // ========== Cases ==========

    loadCases() {
      api.listCases({ policySetId: this.policyId }).then(res => {
        this.cases = (res.data || []).map(c => {
          // Fetch latest run status for each case
          api.listRuns(c.id).then(runRes => {
            const runs = runRes.data || [];
            if (runs.length > 0) {
              c.lastRunStatus = runs[0].resultStatus;
            }
          }).catch(() => {});
          return c;
        });
      });
    },

    defaultCaseForm() {
      return {
        accountSetsId: this.currentAccountSets ? this.currentAccountSets.id : 66,
        policySetId: this.policyId,
        policyCode: this.policy.policyCode,
        caseCode: '',
        caseName: '',
        businessType: 'PURCHASE_PAYMENT',
        description: '',
        requestJson: '',
        expectedDecisionType: null,
        expectedRiskLevel: null,
        expectedMatched: null
      };
    },

    saveCase() {
      if (!this.caseForm.caseCode || !this.caseForm.caseName) {
        this.$message.error('样例编码和名称为必填');
        return;
      }
      let requestData = { ...this.caseForm };
      // Ensure expectedMatched is null for "any" fuzzy matching
      if (requestData.expectedMatched === undefined) requestData.expectedMatched = null;

      if (requestData.id) {
        api.updateCase(requestData.id, requestData).then(() => {
          this.$message.success('更新成功');
          this.showCaseForm = false;
          this.loadCases();
        }).catch(() => { this.$message.error('保存失败，请检查 JSON 格式'); });
      } else {
        api.createCase(requestData).then(() => {
          this.$message.success('创建成功');
          this.showCaseForm = false;
          this.loadCases();
        }).catch(() => { this.$message.error('创建失败，请检查 JSON 格式'); });
      }
    },

    editCase(c) {
      this.caseForm = { ...c };
      this.showCaseForm = true;
    },

    archiveCase(c) {
      this.$confirm('确认归档样例"' + c.caseName + '"?', '提示').then(() => {
        api.archiveCase(c.id).then(() => {
          this.$message.success('归档成功');
          this.loadCases();
        });
      }).catch(() => {});
    },

    runCaseSimulate(c) {
      api.runSimulate(c.id).then(res => {
        const run = res.data;
        this.$message.success('模拟执行完成，结果: ' + (run.resultStatus || 'unknown'));
        this.loadCases();
      }).catch(() => { this.$message.error('模拟执行失败'); });
    },

    runCaseCompare(c) {
      api.runCompare(c.id).then(res => {
        const run = res.data;
        this.$message.success('对比执行完成，结果: ' + (run.resultStatus || 'unknown'));
        this.loadCases();
      }).catch(() => { this.$message.error('对比执行失败'); });
    },

    runBatchCases() {
      this.$confirm('确认批量执行当前策略下所有活跃样例？', '提示').then(() => {
        this.batchRunning = true;
        api.runCases(this.policyId).then(res => {
          this.$message.success('批量回归测试完成，共执行 ' + ((res.data || []).length) + ' 个样例');
          this.loadCases();
        }).catch(() => { this.$message.error('批量执行失败'); })
        .finally(() => { this.batchRunning = false; });
      }).catch(() => {});
    },

    seedStandardCases() {
      this.$confirm('将初始化本策略的默认标准样例（仅用于策略模拟和回归验证，不影响真实凭证生成），确认继续？', '提示').then(() => {
        this.seeding = true;
        api.seedCases(this.policyId).then(res => {
          const d = res.data || {};
          this.$message.success('标准样例初始化完成：新增 ' + (d.created || 0) + ' 个，跳过 ' + (d.skipped || 0) + ' 个');
          this.loadCases();
        }).catch(() => { this.$message.error('初始化失败'); })
        .finally(() => { this.seeding = false; });
      }).catch(() => {});
    },

    viewCaseRuns(c) {
      this.runsCaseName = c.caseName;
      api.listRuns(c.id).then(res => {
        this.runs = res.data || [];
        this.showRuns = true;
      }).catch(() => { this.$message.error('加载执行记录失败'); });
    },

    fillCaseFromSimForm() {
      const evidenceSamples = [];
      if (this.simForm.evidenceJson) {
        try { evidenceSamples.push(...JSON.parse(this.simForm.evidenceJson)); } catch (e) {}
      }
      this.caseForm.requestJson = JSON.stringify({
        accountSetsId: this.currentAccountSets ? this.currentAccountSets.id : 66,
        policyCode: this.policy.policyCode,
        businessType: this.simForm.businessType || undefined,
        paymentAmount: this.simForm.paymentAmount || undefined,
        counterpartyName: this.simForm.counterpartyName || undefined,
        counterpartyEntityId: this.simForm.counterpartyEntityId || undefined,
        period: this.simForm.period || undefined,
        evidenceSamples: evidenceSamples.length > 0 ? evidenceSamples : undefined
      }, null, 2);
    },

    fillCaseTemplate() {
      this.caseForm.requestJson = JSON.stringify({
        accountSetsId: this.currentAccountSets ? this.currentAccountSets.id : 66,
        policyCode: this.policy.policyCode,
        businessType: 'PURCHASE_PAYMENT',
        paymentAmount: '30000.00',
        counterpartyName: '深圳云帆科技有限公司',
        period: '2026-05',
        evidenceSamples: [{
          source: 'REFERENCE_FACT_PURCHASE_PAYABLE_SUMMARY',
          fields: { paymentAmount: '30000.00', endingBalance: '150000.00' }
        }]
      }, null, 2);
    },

    sceneLabel(s) { return { PURCHASE_PAYMENT: '采购付款', LOGISTICS_PAYMENT: '物流付款', BANK_FLOW: '银行流水' }[s] || s; },
    statusTag(s) { return s === 'ACTIVE' ? 'success' : s === 'DRAFT' ? 'info' : 'danger'; },
    statusLabel(s) { return { ACTIVE: '已发布', DRAFT: '草稿', ARCHIVED: '已归档' }[s] || s; },
    dateFormatter(row, col, val) { return val ? val.substring(0, 19).replace('T', ' ') : ''; },
    riskTagType(level) { return level === 'PASS' ? 'success' : level === 'WARNING' ? 'warning' : 'danger'; }
  }
};
</script>

<style scoped>
.policy-detail { padding: 20px; }
.page-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
</style>
